/**
 * Color fijo por procedencia (portal / canal) para chips en Pipeline, Leads, etc.
 * Nombres desconocidos reciben un color estable por hash del texto.
 */

const FALLBACK_COLORS = [
  "#6366f1", "#c026d3", "#0891b2", "#b45309", "#65a30d", "#4338ca",
  "#be185d", "#0e7490", "#a16207", "#4f46e5", "#9d174d", "#155e75",
];

function slugFuente(fuente) {
  return String(fuente ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]/g, "");
}

function hashDjb2(s) {
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  }
  return h;
}

/**
 * Hex de fondo sólido para una procedencia (siempre el mismo texto → mismo color).
 */
export function colorHexProcedencia(fuente) {
  const slug = slugFuente(fuente);
  if (!slug) return "#64748b";

  const rawLo = String(fuente ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  const tokens = rawLo.replace(/[^a-z0-9áéíóúüñ\s]/gi, " ").split(/\s+/).filter(Boolean);

  if (slug.includes("fotocasa")) return "#eab308";
  if (slug.includes("idealista")) return "#f97316";
  if (slug.includes("milanuncios")) return "#ef4444";
  if (slug.includes("habitaclia")) return "#7c3aed";
  if (slug.includes("thinkspain")) return "#0d9488";
  /* TodoPisos contiene "pisos": comprobar antes que Pisos.com / pisos genérico */
  /* Violeta distinto de Habitaclia (#7c3aed) y de Pisos.com (azul) */
  if (slug.includes("todopisos")) return "#9333ea";
  if (slug.includes("pisos")) return "#0284c7";
  if (slug.includes("kyero")) return "#db2777";
  if (slug.includes("vitalcasa")) return "#16a34a";
  if (slug.includes("whatsapp")) return "#128c7e";
  if (tokens.includes("ana")) return "#6366f1";
  /* Redes: azul FB, coral IG, negro TikTok (muy distintos entre sí y del resto).
     No usar slug.includes("meta"): choca con "metadata", "metallic", etc. */
  if (slug.includes("facebook") || slug === "fb" || slug === "meta") return "#1877f2";
  if (slug.includes("instagram")) return "#e4405f";
  if (slug.includes("tiktok")) return "#0f0f0f";
  if (slug.includes("linkedin")) return "#0a66c2";
  if (slug.includes("google")) return "#ea4335";
  if (slug.includes("youtube")) return "#b91c1c";
  if (slug.includes("email") || slug.includes("correo") || slug.includes("mail")) return "#475569";
  if (slug.includes("telefono") || slug.includes("llamada") || slug.includes("ring")) return "#57534e";
  if (slug.includes("web")) return "#15803d";
  if (slug.includes("colaborador")) return "#c2410c";

  return FALLBACK_COLORS[hashDjb2(slug) % FALLBACK_COLORS.length];
}

/**
 * Emoji para mostrar en chips / tabla si en BD viene vacío o "-".
 */
export function emojiVisualProcedencia(nombre, emojiBd) {
  const e = String(emojiBd ?? "").trim();
  if (e && e !== "-") return e;
  const s = slugFuente(nombre);
  if (!s) return "📋";
  if (s.includes("todopisos")) return "📚";
  if (s.includes("pisoscom") || s === "pisos") return "🏬";
  if (s.includes("colaborador")) return "🧩";
  if (s.includes("fotocasa")) return "🔶";
  if (s.includes("idealista")) return "🏠";
  if (s.includes("vitalcasa")) return "🌐";
  if (s.includes("whatsapp")) return "💬";
  if (s.includes("facebook") || s === "fb" || s === "meta") return "📘";
  if (s.includes("instagram")) return "📸";
  if (s.includes("tiktok")) return "🎵";
  if (s.includes("telefono") || s.includes("llamada")) return "📞";
  if (s.includes("referido")) return "👥";
  if (s.includes("cartel")) return "📋";
  if (s.includes("oficina")) return "🏢";
  if (s.includes("milanuncios")) return "📌";
  if (s.includes("habitaclia")) return "🏘️";
  if (s.includes("email") || s.includes("mail")) return "✉️";
  return "📋";
}

/** Texto legible sobre fondo hex #RRGGBB */
export function textOnSolidColor(bgHex) {
  if (!bgHex || typeof bgHex !== "string") return "#fff";
  const m = bgHex.trim().match(/^#([0-9a-f]{6})$/i);
  if (!m) return "#fff";
  const n = parseInt(m[1], 16);
  const r255 = (n >> 16) & 255;
  const g255 = (n >> 8) & 255;
  const b255 = n & 255;
  const r = r255 / 255;
  const g = g255 / 255;
  const b = b255 / 255;
  const lin = (x) => (x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4));
  const R = lin(r);
  const G = lin(g);
  const B = lin(b);
  const lum = 0.2126 * R + 0.7152 * G + 0.0722 * B;

  // Naranjas/ámbar (p. ej. Idealista, colaborador): el blanco queda poco
  // legible aunque la luminancia "cruda" no llegue al umbral general. Se
  // detecta por tono (hue) en vez de hardcodear colores concretos.
  const max = Math.max(r255, g255, b255);
  const min = Math.min(r255, g255, b255);
  const delta = max - min;
  if (delta > 0 && max === r255) {
    let hue = 60 * (((g255 - b255) / delta) % 6);
    if (hue < 0) hue += 360;
    if (hue >= 8 && hue <= 55) return "#0f172a";
  }

  return lum > 0.45 ? "#0f172a" : "#ffffff";
}

/** Procedencias internas del CRM que no deben mostrarse como etiqueta en leads. */
export function fuenteVisibleEnLead(fuente) {
  const s = String(fuente ?? "").trim();
  if (!s) return null;
  if (slugFuente(s) === "fichacliente") return null;
  // Motivo de aviso (bajada), no procedencia (portal / RRSS / boca a boca…).
  if (slugFuente(s) === "bajadaprecio" || /^bajada\s+de\s+precio$/i.test(s)) return null;
  // Producto renombrado: Scraping → MarketPlace (resto del texto igual).
  if (/^Scraping\b/i.test(s)) return s.replace(/^Scraping\b/i, "MarketPlace");
  return s;
}

/** Comparación estable de procedencias (Fotocasa / fotocasa / Web VitalCasa…). */
export function mismaProcedencia(a, b) {
  const sa = slugFuente(a);
  const sb = slugFuente(b);
  return Boolean(sa && sb && sa === sb);
}

/** Estilos inline para chip de procedencia */
export function procedenciaChipStyle(fuente) {
  const bg = colorHexProcedencia(fuente);
  return { background: bg, color: textOnSolidColor(bg), border: "none" };
}
